/**
 * Entity identity.
 *
 * Ids are branded strings rather than plain `string` so that an entity id can never be
 * passed where a label, a layer name or another kind of key is expected. In a codebase
 * where nearly every function signature takes ids, that distinction catches a real class
 * of bug at compile time and costs nothing at runtime.
 */

declare const entityIdBrand: unique symbol;

export type EntityId = string & { readonly [entityIdBrand]: 'EntityId' };

/**
 * Wraps a raw string as an `EntityId`.
 *
 * Reserved for deserialisation and tests — everywhere else, ids come from an
 * `IdGenerator` so that uniqueness is guaranteed rather than assumed.
 */
export function asEntityId(raw: string): EntityId {
  return raw as EntityId;
}

/**
 * Allocates entity ids.
 *
 * Deliberately *not* random. Ids are counter-based and prefixed by kind (`p1`, `seg2`,
 * `c3`) for three reasons that matter over the life of the project:
 *
 * - **Deterministic replay.** Replaying a command log must reproduce the same document,
 *   byte for byte. Random or time-based ids make that impossible to assert in a test.
 * - **Readable files.** A saved document is JSON that a teacher or a support engineer
 *   may open. `"seg12"` is diffable; a UUID is noise.
 * - **Compact documents.** Ids repeat on every parent reference; 36-character UUIDs
 *   would dominate the file size of a large construction.
 *
 * The counter lives in the document, so an id is never reused even across a save and
 * reload that deleted entities in between.
 */
export interface IdGenerator {
  next(prefix: string): EntityId;
  /** Current counter value, persisted with the document. */
  peek(): number;
}

export function createIdGenerator(startAt = 1): IdGenerator {
  let counter = startAt;
  return {
    next(prefix: string): EntityId {
      const id = `${prefix}${String(counter)}`;
      counter += 1;
      return id as EntityId;
    },
    peek(): number {
      return counter;
    },
  };
}
